// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { AtSign } from "lucide-react"

import { sendSharedDocument } from "@crm-src/lib/shareDocument"

/**
 * "Send to myself" — the invoice/quote analogue of the email-campaign
 * **Send Test** button. Opens a tiny dialog pre-filled with the signed-in
 * user's own email so an operator can fire the exact same share email (same
 * subject, same optional note, same attached PDF) into their own inbox to eyeball
 * it — without having to add themselves as a recipient in the Share modal.
 *
 * It reuses the same transport as the Share modal (`sendSharedDocument`), so the
 * self-copy is byte-for-byte what a real recipient would receive.
 */
interface SendToSelfDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentId: string
  documentType: "invoice" | "quote"
  /** The subject/body currently composed in the Share modal, so the self-copy
   *  matches what recipients get verbatim. */
  subject: string
  body: string
  /** Pre-filled with the signed-in user's own email; editable. */
  defaultEmail?: string
}

export default function SendToSelfDialog({
  open,
  onOpenChange,
  documentId,
  documentType,
  subject,
  body,
  defaultEmail = "",
}: SendToSelfDialogProps) {
  const [email, setEmail] = useState(defaultEmail)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const docLabel = documentType === "quote" ? "quote" : "invoice"

  // Reset the form each time the dialog opens.
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setEmail(defaultEmail)
      setError(null)
      setSent(false)
    }
    onOpenChange(newOpen)
  }

  const handleSend = async () => {
    setError(null)

    if (!email.trim()) {
      setError("Email address is required")
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address")
      return
    }

    try {
      setIsSending(true)
      const result = await sendSharedDocument({
        documentId,
        documentType,
        recipients: [{ email: email.trim(), name: email.trim() }],
        subject,
        body,
      })
      if (result.success === false) {
        throw new Error(result.error || `Failed to send ${docLabel}`)
      }
      setSent(true)
      // Give the success note a beat before closing.
      onOpenChange(false)
    } catch (err: any) {
      const data = err?.response?.data
      const msg =
        (Array.isArray(data?.errors) && data.errors[0]) ||
        data?.error ||
        err?.message ||
        `Failed to send ${docLabel}`
      setError(msg)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSending) {
      handleSend()
    }
  }

  return (
    <Dialog open={open} onClose={() => handleOpenChange(false)} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="span" sx={{ display: "flex" }}>
          <AtSign size={20} />
        </Box>
        Send to myself
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Send this {docLabel} to your own inbox to preview exactly what recipients
          will receive — the same subject, note, and attached PDF. It does not go to
          any of the recipients above.
        </DialogContentText>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              id="send-to-self-email"
              label="Your email address"
              type="email"
              size="small"
              fullWidth
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSending}
              autoFocus
              InputLabelProps={{ shrink: true }}
            />
            {error && (
              <Typography variant="body2" sx={{ color: "error.main" }}>
                {error}
              </Typography>
            )}
            {sent && !error && (
              <Typography variant="body2" sx={{ color: "success.main" }}>
                Sent — check your inbox.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.info.main, 0.12),
              border: 1,
              borderColor: (t) => alpha(t.palette.info.main, 0.3),
              p: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ color: "info.dark" }}>
              <strong>Note:</strong> The {docLabel} is sent immediately to the address
              above. This is a self-copy — it does not send to your recipients.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => onOpenChange(false)}
          disabled={isSending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isSending || !email.trim()}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <AtSign size={16} />}
        >
          {isSending ? "Sending..." : "Send to me"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
